import { useEffect, useState } from "react";
import { useTonWallet, useTonConnectUI } from "@tonconnect/ui-react";
import { rpcCall } from "../ws/client";
import DisputeModal from "./DisputeModal";
import ConfirmationModal from "./ConfirmationModal";
import DealChat from "./DealChat";
import { toast } from "./ui/Toast";
import { Button } from "./ui";
import "../styles/MyDeals.scss";
import { useQAProfile, isLocalhost } from "../hooks/useQAProfile";

interface Deal {
    id: string;
    order_id: string;
    seller: string;
    buyer: string;
    jetton_symbol: string;
    jetton_address: string;
    amount: number;
    fiat_currency: string;
    fiat_amount: number;
    price: number;
    status: string;
    current_step: string;
    payment_details_ids?: string[];
    payment_timeout_at?: string;
    release_timeout_at?: string;
    auto_cancel_at?: string;
    created_at: string;
    updated_at: string;
    confirmations?: string[];
    has_dispute?: boolean;
}

// Mapping of deal steps to human-readable Russian names
const DEAL_STEP_NAMES: Record<string, string> = {
    "created": "Сделка создана",
    "escrow_pending": "Ожидание escrow",
    "payment_pending": "Ожидание оплаты",
    "payment_sent": "Оплата отправлена",
    "completed": "Сделка завершена",
    "cancelled": "Сделка отменена"
};

// Mapping of steps to actions for each role
const DEAL_STEP_ACTIONS: Record<string, { seller: string; buyer: string }> = {
    "created": { seller: "Ожидание escrow", buyer: "Ожидание оплаты" },
    "escrow_pending": { seller: "Внесите средства в escrow", buyer: "Ожидание подтверждения escrow" },
    "payment_pending": { seller: "Ожидание оплаты от покупателя", buyer: "Переведите оплату продавцу" },
    "payment_sent": { seller: "Подтвердите получение оплаты", buyer: "Ожидание подтверждения продавцом" },
    "completed": { seller: "Сделка завершена", buyer: "Сделка завершена" },
    "cancelled": { seller: "Сделка отменена", buyer: "Сделка отменена" }
};

// Icons for each step
const DEAL_STEP_ICONS: Record<string, string> = {
    "created": "📋",
    "escrow_pending": "🔒",
    "payment_pending": "💰",
    "payment_sent": "💸",
    "completed": "🎉",
    "cancelled": "❌"
};

function formatTimeRemaining(isoDate: string | undefined): string {
    if (!isoDate) return "—";
    
    const now = new Date();
    const target = new Date(isoDate);
    const diffMs = target.getTime() - now.getTime();
    
    if (diffMs <= 0) return "Истёк";
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
        return `${hours}ч ${minutes}м`;
    }
    return `${minutes}м`;
}

function getTimeWarning(isoDate: string | undefined): "normal" | "warning" | "danger" {
    if (!isoDate) return "normal";
    
    const now = new Date();
    const target = new Date(isoDate);
    const diffMs = target.getTime() - now.getTime();
    const hoursLeft = diffMs / (1000 * 60 * 60);
    
    if (hoursLeft < 1) return "danger"; // Less than 1 hour
    if (hoursLeft < 4) return "warning"; // Less than 4 hours
    return "normal";
}

export default function MyDeals() {
    const wallet = useTonWallet();
    const [tonConnectUI] = useTonConnectUI();
    const { isQAMode, currentWallet: effectiveWallet, toggleQA, profile, switchProfile } = useQAProfile(wallet?.account?.address);
    const [deals, setDeals] = useState<Deal[]>([]);
    const [loading, setLoading] = useState(false);
    const [disputeModal, setDisputeModal] = useState<{ dealId: string; orderId: string } | null>(null);
    const [confirmModal, setConfirmModal] = useState<string | null>(null);
    const [paymentDetails, setPaymentDetails] = useState<Record<string, any>>({});
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [processingOrderId, setProcessingOrderId] = useState<string | null>(null);
    const [confirmingOrderId, setConfirmingOrderId] = useState<string | null>(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [messageModal, setMessageModal] = useState<{ dealId: string; counterpartyRole: string } | null>(null);

    const currentWallet = effectiveWallet || wallet?.account?.address;
    const REAL_USER_WALLET = wallet?.account?.address?.toLowerCase() || "";

    useEffect(() => {
        if (currentWallet) {
            loadDeals();
        }
    }, [currentWallet]);

    const loadDeals = async () => {
        if (!currentWallet) return;

        setLoading(true);
        try {
            let response;
            
            if (isQAMode) {
                // In QA mode, load ALL deals to simulate different profiles
                response = await rpcCall<any, { deals: Deal[] }>(
                    "deals.list_all",
                    {}  // No wallet filter
                );
            } else {
                response = await rpcCall<any, { deals: Deal[] }>(
                    "deals.list",
                    { wallet: currentWallet }
                );
            }

            if (response.result) {
                // Filter deals based on current QA profile
                const filteredDeals = isQAMode 
                    ? filterDealsForProfile(response.result.deals, profile)
                    : response.result.deals;
                
                console.log('[MyDeals] Loaded deals:', filteredDeals.map(d => ({
                    id: d.id,
                    order_id: d.order_id,
                    current_step: d.current_step,
                    seller: d.seller.slice(0, 10),
                    buyer: d.buyer.slice(0, 10)
                })));
                
                setDeals(filteredDeals);
                // Load payment details for all deals
                loadPaymentDetails(filteredDeals);
            }
        } catch (err) {
            console.error("Failed to load deals:", err);
            toast.error("Не удалось загрузить сделки");
        } finally {
            setLoading(false);
        }
    };

    // Filter deals for QA mode based on selected profile
    const filterDealsForProfile = (deals: Deal[], profile: 'seller' | 'buyer'): Deal[] => {
        if (!REAL_USER_WALLET) return [];
        
        console.log('[QA] REAL_USER_WALLET:', REAL_USER_WALLET);
        console.log('[QA] Profile:', profile);
        
        const filtered = deals.filter(deal => {
            const sellerLower = deal.seller.toLowerCase();
            const buyerLower = deal.buyer.toLowerCase();
            const realWalletLower = REAL_USER_WALLET.toLowerCase();
            
            console.log('[QA] Deal seller:', sellerLower);
            console.log('[QA] Deal buyer:', buyerLower);
            
            // Check if real wallet matches either party
            const isRealUserSeller = sellerLower === realWalletLower || sellerLower.includes(realWalletLower.slice(4, 20));
            const isRealUserBuyer = buyerLower === realWalletLower || buyerLower.includes(realWalletLower.slice(4, 20));
            
            console.log('[QA] isRealUserSeller:', isRealUserSeller, 'isRealUserBuyer:', isRealUserBuyer);
            
            if (profile === 'seller') {
                return isRealUserSeller;
            } else {
                return isRealUserBuyer;
            }
        });

        return filtered;
    };

    const loadPaymentDetails = async (deals: Deal[]) => {
        const allDetailIds = new Set<string>();
        deals.forEach(deal => {
            (deal.payment_details_ids || []).forEach(id => allDetailIds.add(id));
        });
        
        console.log('[loadPaymentDetails] All detail IDs to load:', Array.from(allDetailIds));
        
        if (allDetailIds.size === 0) return;
        
        setDetailsLoading(true);
        try {
            const response = await rpcCall<any, { details: any[] }>(
                "payment_details.get_by_ids",
                { ids: Array.from(allDetailIds) }
            );
            
            console.log('[loadPaymentDetails] Response:', response);
            
            if (response.error) {
                console.error('[loadPaymentDetails] Error:', response.error);
                toast.error(`Не удалось загрузить платежные реквизиты: ${response.error.message}`);
                return;
            }
            
            if (response.result?.details) {
                const detailsMap: Record<string, any> = {};
                response.result.details.forEach((d: any) => {
                    detailsMap[d.id] = d;
                });
                console.log('[loadPaymentDetails] Loaded details map:', detailsMap);
                setPaymentDetails(detailsMap);
            }
        } catch (err) {
            console.error("Failed to load payment details:", err);
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleConfirmCompletion = async (orderId: string) => {
        // Найти текущую сделку
        const deal = deals.find(d => d.order_id === orderId);
        if (!deal) {
            toast.error("Сделка не найдена");
            setConfirmModal(null);
            return;
        }

        // Устанавливаем индикатор обработки
        setModalLoading(true);
        setProcessingOrderId(orderId);
        setConfirmingOrderId(orderId);

        // В QA режиме используем адрес из сделки в зависимости от профиля
        // В обычном режиме используем текущий кошелек
        let walletToUse: string;
        if (isQAMode) {
            walletToUse = profile === 'seller' ? deal.seller : deal.buyer;
        } else {
            walletToUse = currentWallet || '';
        }
        
        if (!walletToUse) return;

        const isQASeller = isQAMode && profile === 'seller';
        const isNormalSeller = !isQAMode && deal.seller.toLowerCase() === walletToUse.toLowerCase();
        const isSeller = isQASeller || isNormalSeller;

        // Если это этап escrow_pending и пользователь - продавец
        if ((deal.current_step === "escrow_pending" || deal.current_step === "created") && isSeller) {
            try {
                // Получить данные транзакции для отправки в escrow
                console.log("🔄 Step 1: Requesting transaction data...");
                console.log(isQAMode ? "🔧 QA Mode: Creating real transaction for testing" : "🔐 Production Mode: Creating transaction");
                const txResponse = await rpcCall<
                    { order_id: string; seller_wallet: string; jetton_address: string; amount: number },
                    { success: boolean; transaction?: any; error?: string }
                >("escrow.get_deposit_transaction", {
                    order_id: orderId,
                    seller_wallet: walletToUse,
                    jetton_address: deal.jetton_address,
                    amount: deal.amount
                });

                if (!txResponse.result?.success || !txResponse.result?.transaction) {
                    toast.error(txResponse.result?.error || "Не удалось создать транзакцию");
                    setModalLoading(false);
                    setConfirmModal(null);
                    return;
                }

                // Проверить есть ли сообщения в транзакции
                if (!txResponse.result.transaction.messages || txResponse.result.transaction.messages.length === 0) {
                    console.error("❌ Transaction has no messages!");
                    toast.error("Ошибка: транзакция не содержит сообщений");
                    setModalLoading(false);
                    setConfirmModal(null);
                    return;
                }

                // Получить требуемое количество TON для gas
                const message = txResponse.result.transaction.messages[0];
                const requiredTon = Number(message.amount) / 1_000_000_000; // nanoton to TON
                console.log(`💰 Transaction requires ${requiredTon} TON for gas fees`);
                
                // Предупредить пользователя о необходимости TON
                toast.info(
                    `💰 Для отправки требуется ~${requiredTon} TON для оплаты комиссии сети. Убедитесь что на балансе есть TON!`,
                    { duration: 8000 }
                );

                // Отправить транзакцию через TonConnect
                console.log("🔐 Opening wallet for signing...");
                toast.info("⏳ Пожалуйста, подтвердите транзакцию в вашем кошельке...", { duration: 10000 });
                
                let result;
                try {
                    result = await tonConnectUI.sendTransaction(txResponse.result.transaction);
                    console.log("✅ Step 3: Transaction signed and sent by user!");
                    console.log("Transaction result:", result);
                } catch (txError: any) {
                    console.error("❌ Transaction failed:", txError);
                    if (txError.message?.includes("User rejected") || txError.message?.includes("Wallet operation cancelled")) {
                        toast.warning("❌ Транзакция отклонена");
                    } else {
                        toast.error(`❌ Ошибка при отправке: ${txError.message || "неизвестная ошибка"}`);
                    }
                    setModalLoading(false);
                    setConfirmModal(null);
                    return;
                }
                
                // Извлечь BOC транзакции для получения хэша
                // result.boc содержит подписанную транзакцию в формате BOC (base64)
                const txBoc = result.boc;
                
                if (!txBoc) {
                    console.error("❌ No BOC returned from transaction!");
                    toast.error("Ошибка: не получен BOC транзакции");
                    setModalLoading(false);
                    setConfirmModal(null);
                    return;
                }
                
                console.log("📝 Transaction BOC (first 100 chars):", txBoc?.substring(0, 100));
                console.log("📝 BOC length:", txBoc?.length);

                // Показать пользователю что транзакция отправлена
                toast.success("✅ Транзакция подписана и отправляется в блокчейн...");

                // Подтвердить депозит в escrow с tx_boc
                console.log("🔄 Step 4: Confirming deposit with backend...");
                const confirmResponse = await rpcCall("orders.confirm_completion", {
                    order_id: orderId,
                    wallet: walletToUse,
                    tx_boc: txBoc  // Передаем BOC вместо хэша
                });

                console.log("📨 Confirm response:", confirmResponse);

                if (confirmResponse.error) {
                    toast.error(confirmResponse.error.message);
                } else {
                    console.log("✅ Step 5: Deposit confirmed!");
                    toast.success("✅ Средства внесены в escrow!");
                    loadDeals();
                }
            } catch (err: any) {
                console.error(err);
                if (err.message?.includes("User rejected")) {
                    toast.warning("Транзакция отклонена");
                } else {
                    toast.error("Ошибка при отправке в escrow: " + (err.message || "Неизвестная ошибка"));
                }
            } finally {
                setModalLoading(false);
                setConfirmModal(null);
                setProcessingOrderId(null);
                setConfirmingOrderId(null);
            }
        } else {
            // Для других этапов - обычное подтверждение
            try {
                const response = await rpcCall("orders.confirm_completion", {
                    order_id: orderId,
                    wallet: walletToUse,
                });

                if (response.error) {
                    toast.error(response.error.message);
                } else {
                    toast.success("Подтверждение отправлено");
                    loadDeals();
                }
            } catch (err) {
                console.error(err);
                toast.error("Ошибка при подтверждении");
            } finally {
                setModalLoading(false);
                setConfirmModal(null);
                setProcessingOrderId(null);
                setConfirmingOrderId(null);
            }
        }
    };

    // Show warning only if no wallet connected and QA mode is off
    if (!wallet && !isQAMode) {
        return (
            <div className="my-deals">
                <div className="wallet-warning">
                    <div className="warning-icon">🔐</div>
                    <div className="warning-title">Подключите кошелёк</div>
                    <div className="warning-text">Чтобы увидеть свои сделки, необходимо подключить TON кошелёк</div>
                </div>
            </div>
        );
    }

    return (
        <div className="my-deals">
            {/* QA Mode Controls */}
            {isQAMode && (
                <div className="qa-controls">
                    <div className="qa-toggle">
                        <label className="qa-switch">
                            <input 
                                type="checkbox" 
                                checked={isQAMode}
                                onChange={toggleQA}
                            />
                            <span className="qa-slider"></span>
                        </label>
                        <span className="qa-label">🔧 QA Mode</span>
                    </div>
                    <div className="qa-profile">
                        <span className={`profile-badge ${profile}`}>
                            {profile === 'seller' ? '🏪 Продавец' : '🛒 Покупатель'}
                        </span>
                        <button className="btn btn-small" onClick={switchProfile}>
                            🔄 Сменить профиль
                        </button>
                    </div>
                </div>
            )}

            <div className="list-header">
                <div className="header-actions">
                    <h2>Мои сделки</h2>
                    <Button 
                        variant="secondary" 
                        size="sm"
                        onClick={loadDeals}
                        disabled={loading}
                    >
                        🔄 Обновить
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="loading-container">
                    <div className="spinner" />
                    <span className="loading-text">Загрузка сделок...</span>
                </div>
            ) : deals.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-icon">🤝</div>
                    <div className="empty-state-title">У вас нет сделок</div>
                    <div className="empty-state-description">
                        Начните торговать, создав ордер или приняв существующий
                    </div>
                </div>
            ) : (
                <div className="deals-grid">
                    {deals.map((deal) => {
                        // In QA mode, role is determined by the selected profile
                        // In normal mode, role is determined by the actual wallet
                        const isQASeller = isQAMode && profile === 'seller';
                        const isNormalSeller = !isQAMode && deal.seller.toLowerCase() === currentWallet?.toLowerCase();
                        const isSeller = isQASeller || isNormalSeller;
                        
                        console.log('[QA] isQASeller:', isQASeller, 'isNormalSeller:', isNormalSeller, 'isSeller:', isSeller);
                        
                        const role = isSeller ? "Продавец" : "Покупатель";
                        
                        // Use appropriate wallet for confirmations
                        const confirmationWallet = isQAMode ? REAL_USER_WALLET : currentWallet;
                        const userConfirmed = confirmationWallet ? deal.confirmations?.includes(confirmationWallet) : false;
                        const confirmationsCount = deal.confirmations?.length || 0;
                        
                        const stepName = DEAL_STEP_NAMES[deal.current_step] || deal.current_step;
                        const stepIcon = DEAL_STEP_ICONS[deal.current_step] || "📋";
                        const userAction = isSeller 
                            ? DEAL_STEP_ACTIONS[deal.current_step]?.seller 
                            : DEAL_STEP_ACTIONS[deal.current_step]?.buyer;
                        const timeWarning = getTimeWarning(deal.auto_cancel_at);
                        
                        // Get payment details for the counterparty
                        const counterpartyWallet = isSeller ? deal.buyer : deal.seller;
                        const dealPaymentDetails = (deal.payment_details_ids || [])
                            .map(id => paymentDetails[id])
                            .filter(Boolean);
                        
                        // Check if this deal is currently being processed
                        const isProcessing = processingOrderId === deal.order_id;
                        
                        // Debug logging for payment details
                        if (!isSeller && (deal.current_step === "payment_pending" || deal.current_step === "payment_sent")) {
                            console.log('[Payment Details Debug]', {
                                deal_id: deal.id,
                                payment_details_ids: deal.payment_details_ids,
                                paymentDetails_keys: Object.keys(paymentDetails),
                                dealPaymentDetails_count: dealPaymentDetails.length,
                                dealPaymentDetails: dealPaymentDetails
                            });
                        }

                        return (
                            <div key={deal.id} className={`deal-card status-${deal.status} ${timeWarning === 'danger' ? 'time-danger' : ''} ${timeWarning === 'warning' ? 'time-warning' : ''}`}>
                                {/* Deal Header with Step */}
                                <div className="deal-header">
                                    <div className="step-badge">
                                        <span className="step-icon">{stepIcon}</span>
                                        <span className="step-name">{stepName}</span>
                                    </div>
                                    <span className={`status-badge status-${deal.status}`}>
                                        {deal.status === "in_progress" ? "В процессе" :
                                         deal.status === "completed" ? "Завершена" : "Отменена"}
                                    </span>
                                </div>

                                {/* Role Badge */}
                                <div className="role-row">
                                    <span className={`role-badge ${isSeller ? 'seller' : 'buyer'}`}>
                                        {role}
                                    </span>
                                </div>

                                {/* Main Deal Info */}
                                <div className="deal-body">
                                    <div className="amount-row">
                                        Объем:
                                        <span className="amount">{deal.amount}</span>
                                        <span className="token-symbol">{deal.jetton_symbol}</span>
                                    </div>
                                    <div className="price-row">
                                        <span className="price">Цена: {deal.price} {deal.fiat_currency}</span>
                                    </div>
                                    <div className="total-row">
                                        <span className="total">Сумма: </span>
                                        <span className="total-amount">
                                            <strong>{deal.fiat_amount.toFixed(2)}</strong> {deal.fiat_currency}
                                        </span>
                                    </div>
                                </div>

                                {/* Action Required */}
                                {deal.status === "in_progress" && userAction && (
                                    <div className="action-required">
                                        <span className="action-icon">👉</span>
                                        <span className="action-text">{userAction}</span>
                                    </div>
                                )}

                                {/* Payment Details - показываем покупателю на этапе payment_pending */}
                                {deal.status === "in_progress" && 
                                 !isSeller && 
                                 (deal.current_step === "payment_pending" || deal.current_step === "payment_sent") && 
                                 dealPaymentDetails.length > 0 && (
                                    <div className="payment-details-section">
                                        <div className="section-title">💳 Реквизиты продавца для оплаты:</div>
                                        {dealPaymentDetails.map((detail: any) => (
                                            <div key={detail.id} className="payment-detail-card">
                                                <div className="detail-method">🏦 {detail.payment_method}</div>
                                                {detail.bank_name && <div className="detail-bank">• {detail.bank_name}</div>}
                                                <div className="detail-account">• {detail.account_number}</div>
                                                {detail.account_name && <div className="detail-name">• {detail.account_name}</div>}
                                                {detail.additional_info && <div className="detail-info">{detail.additional_info}</div>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                
                                {/* Предупреждение если у продавца нет реквизитов */}
                                {deal.status === "in_progress" && 
                                !isSeller && 
                                (deal.current_step === "payment_pending" || deal.current_step === "payment_sent") && 
                                dealPaymentDetails.length === 0 && (
                                    <div className="payment-details-section">
                                        <div className="section-title warning">⚠️ Реквизиты продавца недоступны</div>
                                        <div className="detail-account">
                                            Свяжитесь с продавцом для получения реквизитов
                                        </div>
                                    </div>
                                )}

                                {/* Timers */}
                                {deal.status === "in_progress" && (
                                    <div className="timers-section">
                                        {deal.auto_cancel_at && (
                                            <div className={`timer-item ${timeWarning}`}>
                                                <span className="timer-label">⏰ Автоотмена через:</span>
                                                <span className="timer-value">{formatTimeRemaining(deal.auto_cancel_at)}</span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Deal Footer */}
                                <div className="deal-footer">
                                    <small className="date">
                                        Сделка от {new Date(deal.created_at).toLocaleDateString('ru-RU')}
                                    </small>
                                    
                                    {/* Processing Indicator */}
                                    {isProcessing && (
                                        <div className="processing-indicator">
                                            <div className="spinner" />
                                            <span>Обработка...</span>
                                        </div>
                                    )}
                                    
                                    {deal.status === "in_progress" && !isProcessing && (
                                        <div className="deal-actions">
                                            {/* Определяем, может ли текущий пользователь подтверждать на этом этапе */}
                                            {(() => {
                                                // Покупатель не может действовать до внесения средств в escrow
                                                if (!isSeller && (deal.current_step === "escrow_pending" || deal.current_step === "created")) {
                                                    return null;
                                                }
                                                
                                                // Логика подтверждения для каждого этапа
                                                let canConfirm = false;
                                                
                                                if (deal.current_step === "escrow_pending" || deal.current_step === "created") {
                                                    canConfirm = isSeller; // Только продавец
                                                } else if (deal.current_step === "payment_pending") {
                                                    canConfirm = !isSeller; // Только покупатель
                                                } else if (deal.current_step === "payment_sent") {
                                                    canConfirm = isSeller; // Только продавец - после подтверждения сделка завершается
                                                }
                                                
                                                return (
                                                    <>
                                                        {!userConfirmed && canConfirm && (
                                                            <Button
                                                                variant="success"
                                                                size="sm"
                                                                onClick={() => setConfirmModal(deal.order_id)}
                                                                disabled={confirmingOrderId === deal.order_id}
                                                                loading={confirmingOrderId === deal.order_id}
                                                            >
                                                                ✓ Подтвердить
                                                            </Button>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                            
                                            {/* Кнопка чата - доступна обеим сторонам во время активной сделки */}
                                            {deal.status === "in_progress" && (
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    onClick={() => setMessageModal({ 
                                                        dealId: deal.id, 
                                                        counterpartyRole: isSeller ? "Покупатель" : "Продавец"
                                                    })}
                                                >
                                                    💬 Написать
                                                </Button>
                                            )}
                                            
                                            {/* Кнопка спора доступна обеим сторонам после внесения средств в escrow */}
                                            {!deal.has_dispute && (isSeller || (deal.current_step !== "escrow_pending" && deal.current_step !== "created")) && (
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => setDisputeModal({ dealId: deal.id, orderId: deal.order_id })}
                                                >
                                                    ⚠️ Спор
                                                </Button>
                                            )}
                                            
                                            {/* Уведомление для покупателя, ожидающего внесения средств в escrow */}
                                            {!isSeller && (deal.current_step === "escrow_pending" || deal.current_step === "created") && !deal.has_dispute && (
                                                <div className="waiting-escrow-notice">
                                                    <span>⏳ Ожидание внесения средств продавцом в escrow</span>
                                                </div>
                                            )}
                                            
                                            {deal.has_dispute && (
                                                <div className="dispute-notice">
                                                    <span>⚠️ Спор открыт</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            
            {messageModal && currentWallet && (
                <DealChat
                    dealId={messageModal.dealId}
                    currentWallet={currentWallet}
                    counterpartyRole={messageModal.counterpartyRole}
                    onClose={() => setMessageModal(null)}
                />
            )}
            
            {disputeModal && (REAL_USER_WALLET || wallet) && (
                <DisputeModal
                    dealId={disputeModal.dealId}
                    orderId={disputeModal.orderId}
                    wallet={isQAMode ? REAL_USER_WALLET : currentWallet}
                    onClose={() => setDisputeModal(null)}
                    onCreated={loadDeals}
                />
            )}
            
            {confirmModal && (() => {
                const deal = deals.find(d => d.order_id === confirmModal);
                const isQASeller = isQAMode && profile === 'seller';
                const isNormalSeller = !isQAMode && deal?.seller.toLowerCase() === currentWallet?.toLowerCase();
                const isSeller = isQASeller || isNormalSeller;
                const currentStep = deal?.current_step;
                
                let title = "Подтверждение выполнения";
                let message = "Вы подтверждаете, что все условия сделки выполнены?";
                
                // Разные тексты для разных этапов сделки
                if (currentStep === "escrow_pending" || currentStep === "created") {
                    if (isSeller) {
                        title = "Внесение средств в escrow";
                        message = "Вы готовы внести средства в escrow? После подтверждения будет создана транзакция на перевод токенов в escrow-кошелек, где они будут заблокированы до завершения сделки.";
                    }
                } else if (currentStep === "payment_pending") {
                    if (!isSeller) {
                        title = "Подтверждение отправки оплаты";
                        message = "Вы подтверждаете, что отправили фиатную оплату продавцу указанным способом?";
                    }
                } else if (currentStep === "payment_sent") {
                    if (isSeller) {
                        title = "Подтверждение получения оплаты";
                        message = "Вы подтверждаете, что получили фиатную оплату от покупателя? После подтверждения криптовалюта будет автоматически отправлена покупателю из escrow, и сделка завершится.";
                    }
                }
                
                return (
                    <ConfirmationModal
                        title={title}
                        message={message}
                        confirmText="Да, подтверждаю"
                        requireDoubleConfirm={true}
                        loading={modalLoading}
                        onConfirm={() => handleConfirmCompletion(confirmModal)}
                        onCancel={() => {
                            setModalLoading(false);
                            setConfirmModal(null);
                        }}
                    />
                );
            })()}
        </div>
    );
}
